import { CardFlavours } from '@components/Card2/types';
import { cardEditContext } from '@context/card-edit-context';
import { cardModeContext } from '@context/card-mode-context';
import { motion } from 'motion/react';
import { useContext } from 'react';

import { EditCardImageButton } from '../EditCardImageButton';
import type { ImageType, PlaceholderImageSize } from '../types';
import styles from './LeftPicture.module.scss';
import { TemplatePlaceholder } from './TemplatePlaceholder';
import type { CardImage } from '.cache/__types__';

interface LeftPictureProps {
  alt: string;
  canLock: boolean;
  disable?: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  quality: 'high' | 'low' | undefined;
  type: ImageType;
  url: string | undefined | null;
  image: Partial<CardImage> | undefined;
  interactive?: boolean;
  placeholderImageSize: PlaceholderImageSize;
  onImageLoad?: () => void;
}

/** Left picture is always circular */
export const LeftPicture: React.FC<React.PropsWithChildren<LeftPictureProps>> = ({
  alt,
  canLock,
  onClick,
  quality,
  url,
  image,
  type,
  interactive = true,
  placeholderImageSize,
  onImageLoad,
}) => {
  const { flavour } = useContext(cardEditContext);
  const { mode } = useContext(cardModeContext);

  if (mode !== 'edit') {
    // Template images should only NOT show if there is no image or image provider
    if (flavour === CardFlavours.TEMPLATE && !url && !image?.value_provider) {
      return null;
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className={styles['left-picture']}
      data-is-locked={image?.is_locked ?? false}
      data-can-lock={canLock}
      data-edit-mode={Boolean(mode === 'edit')}
      data-has-image={!!url}
      data-image-type={type}
      data-is-interactive={interactive}
    >
      {!url && (
        <div className={styles['left-picture-img']}>
          <TemplatePlaceholder
            type={type}
            isOptional={image?.is_optional}
            position="absolute"
            placeholderImageSize={placeholderImageSize}
            url={url}
            inCardHeader={true}
          />
          {mode === 'edit' && <EditCardImageButton location="right" onClick={onClick} />}
        </div>
      )}
      {url && (
        <>
          <img
            className={styles['left-picture-img']}
            alt={alt}
            src={url}
            data-img-quality={quality}
            onLoad={onImageLoad}
          />
          {mode === 'edit' && !image?.is_locked && <EditCardImageButton location="right" onClick={onClick} />}
        </>
      )}
    </motion.div>
  );
};
